import {
  Box,
  Flex,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { Banner, FormFeedback, Input } from '@morressier/alembic';
import { useMutation } from '@tanstack/react-query';
import { debounce } from 'lodash';
import {
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
  type ReactNode
} from 'react';
import { fetchRinggoldOrganization } from 'src/api/queries';

export type InstitutionSuggestion = {
  name: string;
  ringgoldId: string;
  country?: string | null;
  countryCode?: string | null;
  department?: string | null;
  topLevelOrganizationName?: string | null;
  organizationType?: string | null;
  formattedLocation?: string | null;
};

interface Props {
  name: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (org: InstitutionSuggestion) => void;
  isInvalid?: boolean;
  errorMessage?: string;
  helperText?: ReactNode;
  placeholder?: string;
}

const InstitutionAutosuggest = ({
  name,
  value,
  onChange,
  onSelect,
  isInvalid,
  errorMessage,
  helperText,
  placeholder
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { isOpen, onToggle, onClose } = useDisclosure();

  const {
    data: ringgoldOrgs,
    isLoading,
    mutateAsync: searchRinggoldOrgMutation
  } = useMutation({
    mutationFn: (searchTerm: string) =>
      fetchRinggoldOrganization(searchTerm, 0, 10)
  });

  const handleSearch = useCallback(
    debounce(async (searchTerm: string) => {
      if (searchTerm.length < 3) {
        if (isOpen) onToggle();
        return;
      }

      if (!isOpen) onToggle();
      await searchRinggoldOrgMutation(searchTerm);
    }, 500),
    [isOpen, onToggle, searchRinggoldOrgMutation]
  );

  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        isOpen &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen, onClose]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    onChange(nextValue);
    handleSearch(nextValue);
  };

  const renderAffiliationList = () => {
    if (isLoading) {
      return (
        <Flex flexDirection="column" gap={8} textAlign="center">
          <Box bgColor="neutralSecondaryFill" padding={8}>
            <Spinner fontSize={40} aria-label="Loading affiliation results" />
          </Box>
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingTitle">Loading...</Text>
            <Text textStyle="textBody" paddingX={2}>
              We are currently fetching Affiliation Results from the Ringgold
              database.
            </Text>
          </Flex>
        </Flex>
      );
    }

    if (!isLoading && !ringgoldOrgs?.organizations?.length) {
      return (
        <Banner
          variant="attention"
          heading="No Matching Results?"
          content="If your organization is not already present, please refine your search criteria."
        />
      );
    }

    return (
      <List
        padding={'0 !important'}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <Text textStyle="textContext">
          {ringgoldOrgs.totalCount} Results Found
        </Text>
        {ringgoldOrgs?.organizations.map(org => (
          <ListItem
            as="button"
            type="button"
            role="option"
            aria-label={`Select ${org.name}`}
            cursor="pointer"
            key={org.ringgoldId}
            onClick={() => {
              onSelect(org);
              onClose();
            }}
            data-cy={`ringgold-org-${org.ringgoldId}`}
            paddingY={{ base: 3, md: 1 }}
            paddingX={1}
            textAlign="left"
            width="100%"
            background="transparent"
            border="0"
          >
            <Text textStyle="headingItem">{org.name}</Text>
            {org.topLevelOrganizationName && (
              <Text textStyle="textContext">
                {org.topLevelOrganizationName}
              </Text>
            )}
            {org.formattedLocation && (
              <Text textStyle="textContext">{org.formattedLocation}</Text>
            )}
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Flex direction="column" gap={2}>
      <Box ref={wrapperRef}>
        <Popover
          returnFocusOnClose
          isOpen={isOpen}
          onClose={onClose}
          placement="bottom"
          closeOnBlur={false}
          matchWidth
          autoFocus={false}
        >
          <PopoverTrigger>
            <Input
              ref={inputRef}
              name={name}
              aria-label="Institution"
              aria-haspopup="listbox"
              aria-controls={isOpen ? 'institution-suggestions' : undefined}
              aria-autocomplete="list"
              value={value}
              onChange={handleChange}
              isInvalid={isInvalid}
              placeholder={placeholder}
              data-cy="ringgold-search-input"
              autoComplete="off"
            />
          </PopoverTrigger>
          <PopoverContent
            id="institution-suggestions"
            role="listbox"
            minW={{ base: '100%' }}
            paddingY={4}
            paddingX={2}
            maxHeight={isLoading || !ringgoldOrgs ? 'none' : '264px'}
            overflowY={isLoading || !ringgoldOrgs ? 'unset' : 'auto'}
          >
            <PopoverBody padding={0}>{renderAffiliationList()}</PopoverBody>
          </PopoverContent>
        </Popover>
      </Box>
      {errorMessage ? (
        <FormFeedback variant="error">{errorMessage}</FormFeedback>
      ) : helperText ? (
        <Text textStyle="textContext">{helperText}</Text>
      ) : null}
    </Flex>
  );
};

export default InstitutionAutosuggest;
